// components/SeekerDetails/SeekerDetails.tsx
import React, { useState, useEffect } from "react";
import { X } from "lucide-react";
import { Seeker } from "../../../types/seatApproval";
import styles from "./index.module.scss";
import UserCard from "../../../common/components/UserCard";
import VideoSection from "./Common/VideoSection";
import QuestionsSection from "./Common/QuestionsSection";
import RatingSection from "./Common/RatingSection";
import QuerySection from "./Common/Questions";
import Drawer from "@mui/material/Drawer";
import graphImage from "../../../assets/images/yearGraph.png";
import { Button } from "../../../common/components/Button";
import videoImage from "../../../assets/images/videoImg.png";
import shuffle from "../../../assets/images/shuffle-img.svg";

import { sortPreferences, transformSeekerResponse } from "../../../utils/dataMapper";
import TabsComponent from "../../AllProgramsList/SeekerDashboardHdb/TabsComponent";
import CommonPlaneTabs from "../../../common/components/CommonPlaneTabs";
import MessageSection from "../../MessageSection";
import BackArrow from "../../../assets/images/session_overlay_back_arrow.svg";
import SessionsCard from "./programSessions";
import { useResponsive } from "../../../utils/functions";
import MahatriaBlessCard from "../../../common/components/MahatriaBlessCard";
import { ApprovalStatus, RecommendationText, SEEKER_ASSOCIATION} from "../../../constants/textConstants"
import UseResize from "../../../common/components/UseResize";
import ActivityLog from "../../../common/components/ActivityLog";
import { formatDateOfBirth } from "../../../utils/commonFunctions";
import TimelineChart from "../../../common/components/ExperienceChart";
import GrayLine from "../../../common/components/GrayLine";
import { colorizeMahatriaInfinitheism } from "../../../common/components/ColorizeMahatriaInfinitheism";
import { getCall } from "../../../services/apiService";
import { endPoints, PORTAL } from "../../../constants/urlConstants";
import App from "../../../App";

interface Session {
  id: string | number;
  name: string;
  type: string;
  assignedUsers: any[];
  allocatedCount: number;
  totalSeekers: number;
}
interface SeekerDetailsProps {
  isOpen: boolean;
  onClose: () => void;
  seekerDetails: Seeker | null;
  getInitials?: (name: string) => string;
  isGroupedProgram?: boolean; // Optional prop to indicate if the program is grouped
  sessions: Session[]; // Replace with actual type if available
  onBless: (sessionId: number) => void; // Function to handle blessing a
  user: any;
  handleBlessed?: (
    seekerId: number,
    targetProgramId: number,
    swapType: string,
    swapRequestId?: number,
  ) => void;
  currentProgram?: string;
  availablePrograms?: Session[];
  onProgramSelect?: (program: any) => void;
  onCancel?: () => void;
  programId?: string;
  handleSwapUser?: (user: any, programId: any) => void;
  selectedSwapProgram?: any;
  selectedSwapUser?: any;
  selectedOption?: any; // Replace with actual type if available
  onBlessButtonClick?: () => void; // New prop added
}

const SeekerDetails: React.FC<SeekerDetailsProps> = ({
  isOpen,
  onClose,
  seekerDetails,
  getInitials,
  isGroupedProgram,
  sessions,
  onBless,
  user,
  handleBlessed,
  currentProgram,
  availablePrograms,
  onProgramSelect,
  onCancel,
  programId,
  handleSwapUser,
  selectedSwapProgram,
  selectedSwapUser,
  selectedOption,
  onBlessButtonClick,
}) => {
  const [loading, setLoading] = useState(false);
  const [seekerData, setSeekerData] = useState<Seeker | null>(seekerDetails);
  const [selectedTab, setSelectedTab] = useState<number>(0);

  const passion = 2;
  const growth = 3;
  const infiniteness = 3;
  const continuity = 4;
  const overall = ((passion + growth + infiniteness + continuity) / 4).toFixed(
    1,
  );
  const seekerRegDetails = [
    { key: "Date of birth", value: "dob" },
    { key: "Mobile number", value: "mobileNumber" },
    { key: "Email address", value: "emailAddress" },
    { key: "infinitheism contact person", value: "rmContactUser?.orgUsrName" },
    { key: "Country", value: "countryName" },
    { key: "Since when have you been associated with infinitheism",   value: "hdbAssociationSince"},
    { key: "When was your last HDB/ MSD", value: "lastHdbAttended" },
  ];

  const mockSeekerData: Seeker = {
    name: "Anjan Kumar",
    gender: "M",
    age: 34,
    programs: "03 Programs",
    location: "Chennai",
    appliedDate: "24 Jan 2023, 16:19:07",
    hdbLevels: ["HDB 1", "HDB 2", "HDB 3", "MSD 1", "MSD 2"],
    completedLevels: {
      "HDB 2": 1,
      "HDB 3": 2,
      "MSD 1": 1,
    },
    // journeyYears: "3yrs of his infini journey",
    journeyYears: "Last 3 years of seeker's HDB/MSD journey",
    profileImage: "/api/placeholder/300/400",
    videoThumbnail: "/api/placeholder/300/200",
    roommate: {
      name: "Praveen Yadav",
      preference: "Roommate preference",
    },
    questions: [
      {
        id: 1,
        question: "What are your 3 most dominant emotions?",
        answer:
          "Lorem ipsum Lorem ipsum Lorem ipsum Lorem ipsum Lorem ipsum Lorem ipsum",
      },
      {
        id: 2,
        question: "Mention any 3 qualities that best describe you?",
        answer:
          "Lorem ipsum Lorem ipsum Lorem ipsum Lorem ipsum Lorem ipsum Lorem ipsum",
      },
    ],
    rating: {
      overall,
      passion,
      growth,
      infiniteness,
      continuity,
    },
    review:
      "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Nulla aliquam bibendum dui, nec lacinia magna. Aliquam lacinia augue ut ante consequat faucibus. Fusce rutrum, lorem vitae dictum dapibus.",
    reviewer: "Praveen Kumar assigned as a RM for Anjan Kumar",
  };

  // Optimized helper to safely get nested value by path string (supports dot notation and optional chaining)
  const getValueByPath = (obj: any, path: string) => {
    if (!obj || !path) return "";
    // Remove optional chaining and split by dot
    const keys = path.replace(/\?\./g, ".").split(".");
    return keys.reduce((acc, key) => (acc && acc[key] !== undefined ? acc[key] : ""), obj);
  };

  
  useEffect(() => {
    if (isOpen && !seekerData) {
      fetchSeekerData();
    }
  }, [isOpen, seekerData]);

  const fetchSeekerData = async () => {
    setLoading(true);
    try {
      setTimeout(() => {
        setSeekerData(seekerDetails);
        setLoading(false);
      }, 500);
    } catch (error) {
      console.error("Error fetching seeker data:", error);
      setLoading(false);
    }
  };

  const handlePlayVideo = () => {
  };

  const handleShowAllQuestions = () => {
  };

  const handleSendQuery = () => {
    onBlessButtonClick?.();
  };

  if (!isOpen) return null;

  const [preferenceData, setPreferenceData] = useState<string[]>([]);

  useEffect(() => {
    if (seekerData) {
      setPreferenceData(sortPreferences(seekerData.preferences || [], true));
    }
  }, [seekerData]);
  const handleTabChange = (newValue: number) => {
    setSelectedTab(newValue);
  };
  const transformedSeeker = transformSeekerResponse(seekerData);
  const { isMobileResolution, isTabResolution } = UseResize();
  return (
    <Drawer anchor={"right"} open={isOpen} onClose={onClose}>
      <div className={styles.seekerOverlay}>
        <div
          className={
            seekerDetails?.approvals?.[0]?.approvalStatus === ApprovalStatus.CANCELLED
      ? `${styles.seekerDetailsCancelledModal} ${isOpen ? styles.open : ""}` 
      : `${styles.seekerDetailsModal} ${isOpen ? styles.open : ""}` 
          }
        >
          <div className={styles.modalHeader}>
            <img
              src={BackArrow}
              alt="Back"
              className={styles.backButton}
              onClick={onClose}
            />
            <span className={styles.modalTitle}>Quick view of seeker</span>
            <div className={styles.headerDivider} />
            <X size={26} onClick={onClose} className={styles.closeButton} />
          </div>

          <div className={styles.modalContent}>
            {loading ? (
              <div className={styles.loading}>Loading...</div>
            ) : seekerData ? (
              <>
                <div className={styles.userCard}>
                  <UserCard
                    seeker={seekerData}
                    preferenceData={preferenceData}
                    sessions={sessions}
                    onBless={onBless}
                    onClose={onClose}
                    user={transformSeekerResponse(seekerData)}
                    handleBlessed={handleBlessed}
                    currentProgram={currentProgram}
                    availablePrograms={availablePrograms}
                    onProgramSelect={onProgramSelect}
                    onCancel={onCancel}
                    programId={programId}
                    selectedSwapProgram={selectedSwapProgram}
                    selectedSwapUser={selectedSwapUser}
                    handleSwapUser={handleSwapUser}
                    selectedOption={selectedOption}
                    hideRoomatePreference={true}
                    isOverlay={false}
                    isSeekerOverlay={true}
                  />
                </div>
                {(isMobileResolution || isTabResolution) && (
                  <div className={styles.roommatePreferenceRow}>
                    <div className={styles.roommateDot}></div>
                    <span className={styles.roommateLabel}>
                      Roommate preference
                    </span>
                    <span className={styles.roommateName}>
                      {transformedSeeker.preferredRoomMate}
                    </span>
                  </div>
                )}
                <div className={styles.tabsContainer}>
                  <CommonPlaneTabs
                    tabs={[
                      { label: "Details" },
                      { label: "Messages" },
                      { label: "Activity Log" },
                    ]}
                    onTabChange={handleTabChange}
                  />
                </div>
                {selectedTab === 0 && (
                  <div className={styles.modalContent}>
                    <div className={styles.regDetails}>
                      {seekerRegDetails.map((item, idx) => {
                        let value = getValueByPath(seekerData, item.value);
                        if (item.key === "Date of birth" && value) {
                          value = formatDateOfBirth(value);
                        }
                        if (!value) return null;
                        return (
                          <div key={idx}>
                            <p className={styles.regHeading}>{colorizeMahatriaInfinitheism(item.key)}</p>
                            <p className={styles.regValue}>{colorizeMahatriaInfinitheism(value)}</p>
                          </div>
                        );
                      })}
                    </div>
                    <VideoSection
                      videoSrc={seekerData.videoUrl || ""}
                      className={styles.timeline}
                    />
    
                    
                      <p className={styles.seekerAssociation}>{SEEKER_ASSOCIATION}</p>
                      <GrayLine/>
                    <TimelineChart seekerId={seekerDetails?.user?.id} gender={seekerDetails?.gender} />
                   
                         {/* Question section */}
                    {seekerData.questionResponses &&
                      seekerData.questionResponses.length > 0 && (
                        <QuestionsSection
                          questions={seekerData.questionResponses}
                          onShowAll={handleShowAllQuestions}
                        />
                      )}
                       {/* Swap Requests Display */}
                    {Array.isArray(seekerDetails?.swapsRequests) &&
                      seekerDetails.swapsRequests.length > 0 &&
                      (() => {
                        // Find the most recent swap request by createdAt
                        const latestSwap = seekerDetails.swapsRequests.reduce(
                          (latest, curr) => {
                            return new Date(curr.createdAt) >
                              new Date(latest.createdAt)
                              ? curr
                              : latest;
                          },
                          seekerDetails.swapsRequests[0],
                        );
                        // Get allocated program name
                        const allocatedProgramName =
                          seekerDetails?.allocatedProgram?.name || "";
                        // Get requested program names
                        const requestedProgramNames = (
                          latestSwap.requestedPrograms || []
                        )
                          .map((p: any) => p.name)
                          .filter(Boolean)
                          .join(", ");
                        const swapComment = latestSwap.comment;
                        // Only show if both are present
                        if (allocatedProgramName && requestedProgramNames) {
                          return (
                            <div>
                               <h3 className={styles.swapRequestSectionName}>Swap request</h3>

                              <GrayLine/>
                            <div className={styles.swapRequestSection}>
                             
                              <p className={styles.swapRequestLabel}>
                                swap {allocatedProgramName}{" "}
                                <span>
                                  <img src={shuffle} alt="shuffle" />
                                </span>
                                <span className={styles.reqPrograms}>
                                  {requestedProgramNames}
                                </span>
                              </p>
                              {swapComment && (
                                <p className={styles.swapRequestComment}>
                                  {swapComment
                                    .split("\n")
                                    .map((line: string, idx: number, arr: string[]) => (
                                      <React.Fragment key={idx}>
                                        {line}
                                        {idx < arr.length - 1 && <br />}
                                      </React.Fragment>
                                    ))}
                                </p>
                              )}
                            </div>
                            </div>
                          );
                        }
                        return null;
                      })()}
                    <RatingSection
                      rating={seekerDetails?.averageRating}
                      ratings={seekerDetails.ratings}
                      rmData={seekerDetails?.rmContactUser}
                      rmReview={seekerDetails?.rmReview}
                      prevRating={seekerDetails?.prevRating}
                    />

                    {transformedSeeker?.recommendation && (
                      <div className={styles.recommendationLine}>
                        <p className={styles.recommendationOption}>
                          {transformedSeeker?.recommendation}{" "}
                          {RecommendationText}
                        </p>
                        {transformedSeeker?.recommendationText && (
                          <p>{colorizeMahatriaInfinitheism(transformedSeeker?.recommendationText)}</p>
                        )}
                      </div>
                    )}
                  </div>
                )}
                {selectedTab === 1 && (
                  <MessageSection
                    data={seekerDetails}
                    defaultOpen={true}
                    messageSectionClass={styles.messageConstainer}
                    isRequiredHeader={false}
                  ></MessageSection>
                )}
                {selectedTab === 2 && (
                  <ActivityLog registrationId={seekerDetails?.id} />
                )}
              </>
            ) : (
              <div className={styles.error}>Failed to load seeker data</div>
            )}
          </div>
        </div>
        {!(seekerDetails?.approvals?.[0]?.approvalStatus === ApprovalStatus.CANCELLED) && (
        <div className={styles.footer}>
          <Button
            buttonClassName={styles.sendButtonText}
            buttonTextClassName={styles.sendButtonText}
            onClick={handleSendQuery}
          >
            bless
          </Button>
        </div>)}
      </div>
    </Drawer>
  );
};

export default SeekerDetails;
